// AlbumsPage.jsx
// Landing Page: Travel Photo Albums - Automatic Organization
// Slug: /albums

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPages.css';

// SEO Meta Tags
const SEO = {
  title: 'Automatic Travel Albums | Organize Photos by Trip | VivaJour',
  description:
    'Transform your camera roll into organized travel albums automatically. VivaJour groups photos by trip, date, and location to create story-based albums. On-device processing keeps photos private.',
  slug: '/albums'
};

export default function AlbumsPage() {
  useEffect(() => {
    document.title = SEO.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = SEO.description;
  }, []);

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="landing-nav-logo">
            <span>VivaJour</span>
          </Link>
          <div className="landing-nav-links">
            <Link to="/privacy-first" className="landing-nav-link">Privacy</Link>
            <Link to="/current-trip" className="landing-nav-link">Current Trip</Link>
            <Link to="/albums" className="landing-nav-link active">Albums</Link>
            <Link to="/how-it-works" className="landing-nav-link">How It Works</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <span className="landing-hero-badge">📚 Comprehensive Travel Albums</span>
        <h1>Turn Your Camera Roll<br />Into Beautiful Travel Albums</h1>
        <p className="landing-hero-subtitle">
          Came back from your trip with hundreds of photos? Select your trip photos and let VivaJour do the rest. Automatic organization by date and location creates comprehensive albums that tell your complete travel story.
        </p>
        <div className="landing-cta-group">
          <Link to="/how-it-works" className="landing-btn landing-btn-primary">
            See How It Works →
          </Link>
        </div>
        <div className="landing-trust-row">
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Automatic organization
          </span>
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Photos stay on device
          </span>
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Export as PDF
          </span>
        </div>
      </section>

      {/* The Problem */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container-narrow">
          <h2>Your Photos Deserve Better Than Chronological Scroll</h2>
          <p style={{ color: 'var(--landing-muted)', fontSize: '16px', lineHeight: '1.7', marginTop: '20px' }}>
            You come back from a trip with hundreds of photos scattered across your camera roll. Mixed with selfies, screenshots, and random captures. Finding the story in that chaos takes hours.
          </p>
          <p style={{ color: 'var(--landing-text)', fontSize: '17px', lineHeight: '1.7', marginTop: '20px', fontWeight: 600 }}>
            VivaJour reads the GPS and date data embedded in your photos to automatically group them by location and time — transforming chaos into a coherent travel story.
          </p>
        </div>
      </section>

      {/* How Albums Work */}
      <section className="landing-section">
        <div className="landing-container">
          <h2>How Automatic Albums Work</h2>
          <p className="landing-section-subtitle">
            Three steps from camera roll to shareable album.
          </p>

          <div className="landing-steps">
            <div className="landing-step">
              <div className="landing-step-number">1</div>
              <div className="landing-step-content">
                <h3>Select Trip Photos</h3>
                <p>Choose photos from your camera roll. Select a date range, pick manually, or let VivaJour detect trips based on travel patterns in your metadata.</p>
              </div>
            </div>

            <div className="landing-step">
              <div className="landing-step-number">2</div>
              <div className="landing-step-content">
                <h3>Automatic Organization</h3>
                <p>VivaJour reads each photo&apos;s date and GPS data, clusters nearby locations together, and arranges everything chronologically. Your trip unfolds as a story.</p>
                <p className="landing-step-note">
                  Place names are resolved using your device’s built-in geocoding services; VivaJour does not send location data to its own servers.
                </p>
              </div>
            </div>

            <div className="landing-step">
              <div className="landing-step-number">3</div>
              <div className="landing-step-content">
                <h3>Customize &amp; Export</h3>
                <p>Review your album, reorder photos, add captions, choose a template. Export as PDF or share via link when you are happy.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes It Different */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2 style={{ textAlign: 'center' }}>More Than Just Photo Sorting</h2>
          <p className="landing-section-subtitle" style={{ textAlign: 'center', margin: '0 auto 40px' }}>
            VivaJour creates story-based albums, not just photo grids.
          </p>

          <div className="landing-features-grid">
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📍</div>
              <h3>Location-Based Sections</h3>
              <p>Photos automatically grouped by where you were. Each location becomes a chapter in your travel story.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">📅</div>
              <h3>Chronological Flow</h3>
              <p>Your trip unfolds in order — from departure to return. See your journey as it actually happened.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🏷️</div>
              <h3>Smart Place Names</h3>
              <p>GPS coordinates become readable names: &quot;Day 3 in Barcelona&quot; instead of &quot;41.3851° N, 2.1734° E&quot;.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🗺️</div>
              <h3>Trip Map Overview</h3>
              <p>See your entire journey on a single map. Every chapter and location is plotted so you can visually follow your route from start to finish.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">📌</div>
              <h3>Maps Inside Each Chapter</h3>
              <p>Every chapter begins with its own map, showing exactly where those moments happened before you dive into the photos.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">📊</div>
              <h3>Trip Stats &amp; Highlights</h3>
              <p>Automatically generated stats like number of days, places visited, chapters created, and photos captured — a quick snapshot of your journey at a glance.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">✏️</div>
              <h3>Full Editing Control</h3>
              <p>The automatic organization is a starting point. Reorder, add, remove, caption — make it yours.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🎨</div>
              <h3>Professional Templates</h3>
              <p>Curated layouts designed for travel storytelling. Your photos look polished without design skills.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🔒</div>
              <h3>Privacy Protected</h3>
              <p>All organization happens on your device. No cloud uploads. No AI training on your memories.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="landing-section">
        <div className="landing-container">
          <h2>Perfect For Any Trip — Or a Lifetime of Them</h2>

          <div className="landing-use-cases" style={{ marginTop: '40px' }}>
            <div className="landing-use-case">
              <p className="landing-use-case-title">Weekend Getaways</p>
              <p className="landing-use-case-desc">Quick trips with 50-100 photos. Album ready in minutes.</p>
            </div>

            <div className="landing-use-case">
              <p className="landing-use-case-title">Multi-City Tours</p>
              <p className="landing-use-case-desc">Automatic location detection separates Paris from Rome from Barcelona.</p>
            </div>

            <div className="landing-use-case">
              <p className="landing-use-case-title">Road Trips</p>
              <p className="landing-use-case-desc">Stops along the way become natural chapters in your story.</p>
            </div>

            <div className="landing-use-case">
              <p className="landing-use-case-title">Family Vacations</p>
              <p className="landing-use-case-desc">Combine photos from the whole trip into one shareable album.</p>
            </div>

            <div className="landing-use-case">
              <p className="landing-use-case-title">Adventure Travel</p>
              <p className="landing-use-case-desc">Hikes, dives, climbs — GPS tracks your journey automatically.</p>
            </div>

            <div className="landing-use-case">
              <p className="landing-use-case-title">Old Trips</p>
              <p className="landing-use-case-desc">Finally organize those photos from years ago. Metadata still works.</p>
            </div>

            <div className="landing-use-case">
              <p className="landing-use-case-title">Multiple Albums, Multiple Vibes</p>
              <p className="landing-use-case-desc">Create different albums from the same trip with different themes - or build separate albums for different trips. Your travel library, your way.</p>
            </div>

            <div className="landing-use-case">
              <p className="landing-use-case-title">A Year of Memories</p>
              <p className="landing-use-case-desc">Turn an entire year into chapters — weekends, vacations, milestones — and see how your story unfolded over time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Export Options */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2>Share Your Story</h2>
          <p className="landing-section-subtitle">
            Multiple ways to share and preserve your albums.
          </p>

          <div className="landing-features-grid" style={{ marginTop: '32px' }}>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📄</div>
              <h3>PDF Album Export</h3>
              <p>Export your album as a high-quality digital PDF you can keep forever. View it on any device, archive it safely, or print it if you choose.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🔗</div>
              <h3>Shareable Links</h3>
              <p>Generate a private link to share with friends and family. They can view without installing anything.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">📱</div>
              <h3>Story Mode</h3>
              <p>View your album as an auto-playing story. Great for showing off your trip on a phone or tablet.</p>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <Link to="/templates" className="landing-btn landing-btn-secondary">
              See Templates &amp; Export Options →
            </Link>
          </div>
        </div>
      </section>

      {/* Why Create Comprehensive Albums */}
      <section className="landing-section">
        <div className="landing-container">
          <h2 style={{ textAlign: 'center' }}>Why Create a Comprehensive Travel Album?</h2>
          <p className="landing-section-subtitle">
            Transform hundreds of scattered photos into one beautifully organized story
          </p>

          <div className="landing-features-grid" style={{ marginTop: '40px' }}>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📖</div>
              <h3>Complete Story Arc</h3>
              <p>See your entire journey from beginning to end. Every location, every moment, organized chronologically into a cohesive narrative.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🎁</div>
              <h3>Perfect for Sharing & Gifting</h3>
              <p>Create a beautiful keepsake to share with family or gift to travel companions. Professional quality without the professional price.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">💾</div>
              <h3>Permanent Digital Archive</h3>
              <p>Export as high-quality PDF. Keep it forever, independent of any app or service. Your memories, truly yours.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🖨️</div>
              <h3>Print-Ready Quality</h3>
              <p>Professional templates designed for printing. Take your digital album to physical form whenever you're ready.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🧭</div>
              <h3>Relive the Journey</h3>
              <p>Maps, stats, and location-based chapters let you experience the trip again - not just see photos, but remember the flow of your adventure.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">⏱️</div>
              <h3>Worth the Time Investment</h3>
              <p>30-60 minutes to journal and polish your album. Do it once, enjoy it forever.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🎨</div>
              <h3>Create Multiple Versions</h3>
              <p>Not limited to one album per trip. Create different versions with different vibes - one highlighting people, another showcasing highlights, a comprehensive version. Archive them all digitally.</p>
            </div>
          </div>

          {/* Current Trip Callout - 20% mention */}
          <div
            className="landing-callout"
            style={{
              marginTop: '60px',
              background: 'var(--landing-surface)',
              padding: '28px 32px',
              borderRadius: '12px',
              border: '2px solid var(--landing-border)',
              borderLeft: '4px solid #4A90E2'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
              <div style={{ fontSize: '32px', flexShrink: 0 }}>📍</div>
              <div style={{ flex: 1 }}>
                <h4 style={{ marginBottom: '10px', fontSize: '18px', color: 'var(--landing-text)' }}>
                  Need something faster while traveling?
                </h4>
                <p style={{ color: 'var(--landing-muted)', lineHeight: '1.6', marginBottom: '12px' }}>
                  <strong>Current Trip mode</strong> lets you create quick 10-page daily digests to share with family as you go. Takes 2 minutes per day, prevents photo backlog, and complements your comprehensive album when you return.
                </p>
                <Link 
                  to="/current-trip" 
                  className="landing-btn landing-btn-secondary" 
                  style={{ display: 'inline-block', marginTop: '8px', fontSize: '14px', padding: '8px 20px' }}
                >
                  Learn About Current Trip Mode →
                </Link>
              </div>
            </div>
          </div>

          <p style={{ color: 'var(--landing-muted)', fontSize: '14px', marginTop: '30px', textAlign: 'center' }}>
            Both modes use the same on-device processing and privacy protections.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container-narrow">
          <h2 style={{ textAlign: 'center' }}>Frequently Asked Questions</h2>

          <div className="landing-faq">
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">How does VivaJour detect trips automatically?</h4>
              <p className="landing-faq-answer">VivaJour looks at gaps in your photo timeline and significant changes in GPS location. A cluster of photos far from home, separated by time from your usual photos, indicates a trip.</p>
            </div>

            <div className="landing-faq-item">
              <h4 className="landing-faq-question">What if some photos do not have location data?</h4>
              <p className="landing-faq-answer">Photos without GPS are organized by date. They will appear in chronological order, and you can manually assign locations or group them into sections.</p>
            </div>

            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Can I combine photos from different devices?</h4>
              <p className="landing-faq-answer">Yes, if the photos are on the same device when you create the album. Transfer photos from other devices first, then select them all in VivaJour.</p>
            </div>

            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Is there a limit on photos per album?</h4>
              <p className="landing-faq-answer">No hard limit. Large albums (500+ photos) may take longer to process. For best results, curate your selection to the best shots.</p>
            </div>

            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Can I create multiple albums from the same trip?</h4>
              <p className="landing-faq-answer">Absolutely! You're not limited to one album per trip. Create different versions with different vibes - a "people-focused" album, a "highlights-only" version, or a comprehensive storytelling edition. Each can use different photo selections, templates, and styles. Archive all versions digitally and choose which to share or print.</p>
            </div>

            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Are my albums saved automatically?</h4>
              <p className="landing-faq-answer">Yes. Albums are saved locally on your device and you can archive as many as you want. They persist between app sessions but are not synced to any cloud. Export to PDF to create a permanent, portable digital copy you can keep forever.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-callout">
            <h3>Create Your Comprehensive Travel Album</h3>
            <p>Automatic organization. Professional templates. Complete privacy.</p>
            <div className="landing-cta-group">
              <a href="https://apps.apple.com/app/id6756980207" className="landing-btn landing-btn-primary" target="_blank" rel="noopener noreferrer">
                Get Started on iOS
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.yourco.traveltracker" className="landing-btn landing-btn-primary" target="_blank" rel="noopener noreferrer">
                Get Started on Android
              </a>
            </div>
          </div>

          {/* Internal Links */}
          <div className="landing-internal-links">
            <Link to="/templates" className="landing-internal-link">
              🎨 Templates — Professional album layouts
            </Link>
            <Link to="/current-trip" className="landing-internal-link">
              📍 Current Trip — Quick daily digests while traveling
            </Link>
            <Link to="/privacy-first" className="landing-internal-link">
              🔒 Privacy-First — How we protect your photos
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-links">
          <Link to="/privacy-first" className="landing-footer-link">Privacy</Link>
          <Link to="/current-trip" className="landing-footer-link">Current Trip</Link>
          <Link to="/albums" className="landing-footer-link">Albums</Link>
          <Link to="/how-it-works" className="landing-footer-link">How It Works</Link>
          <Link to="/templates" className="landing-footer-link">Templates</Link>
        </div>
        <p className="landing-footer-meta">
          VivaJour Labs LLC • Built in Wisconsin
        </p>
        <p className="landing-footer-meta" style={{ marginTop: '8px' }}>
          <Link to="/privacy-policy" className="landing-footer-link">Privacy Policy</Link>
          {' • '}
          <Link to="/terms" className="landing-footer-link">Terms of Service</Link>
          {' • '}
          <Link to="/contact" className="landing-footer-link">Contact</Link>
        </p>
        <p className="landing-footer-tagline">
          © 2026 VivaJour. Your photos stay on your device. Your memories stay yours.
        </p>
      </footer>
    </div>
  );
}